import { useCallback, useLayoutEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DataTable } from '@/components/DataTable';
import { useMediaQuery } from '@/hooks';
import { PAYMENT_STATUS, PAYMENT_TYPE } from '@/models/payment';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { getPriceById } from '@/utils/price';

import { useHandleRetry } from '../../hooks';
import { MobileCell } from './MobileCell';
import PaymentStatusChip from './PaymentStatusChip';
import {
	BillingRangeCell,
	ButtonsCell,
	DateCell,
	ReferenceCell,
	ValueCell
} from './components';

function Table({ history, ready }) {
	const { t } = useTranslation();
	const [grouping, setGrouping] = useState([]);
	const mdDown = useMediaQuery('mdDown');
	const handleRetry = useHandleRetry();
	const { status: subscriptionStatus } = useSelector(
		state => state.profile.subscriptionInfo
	);

	const currentPage = useSelector(state => state.billing?.page);

	const prices = useSelector(state => state.checkout?.prices || []);

	const ButtonsCellComponent = useCallback(
		row => <ButtonsCell row={row} />,
		[]
	);

	const StatusCellComponent = useCallback(row => {
		const currentPrice = getPriceById(row.original.priceId, prices);

		const switchFailed =
			row.original.status === PAYMENT_STATUS.ERROR &&
			[SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIALING].includes(
				subscriptionStatus
			) &&
			currentPage === 1 &&
			row.index === 0;

		const hasPayAction =
			row.original.status === PAYMENT_STATUS.ERROR &&
			row.original.type === PAYMENT_TYPE.PAYMENT &&
			(subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE ||
				switchFailed) &&
			!!(currentPrice?.active || !row.original.priceId);

		const chipRender = (
			<PaymentStatusChip
				type={row.original.type}
				status={row.original.status}
				hasPayAction={hasPayAction}
			/>
		);

		if (hasPayAction) {
			return (
				<button
					type="button"
					onClick={() =>
						handleRetry({
							priceId: row.original.priceId,
							handlerId: row.original.handlerId,
							currency: row.original.currency
						})
					}
				>
					{chipRender}
				</button>
			);
		}

		return chipRender;
	}, []);

	const MobileCellComponent = useCallback(row => <MobileCell row={row} />, []);

	const columns = [
		{
			id: 'date',
			header: t('billing.subscription.table.date'),
			cell: DateCell
		},
		{
			accessorKey: 'reference',
			header: t('billing.subscription.table.reference'),
			cell: ReferenceCell
		},
		{
			accessorKey: 'billingRange',
			header: t('billing.subscription.table.billingRange.title'),
			cell: BillingRangeCell
		},
		{
			accessorKey: 'amount',
			header: t('billing.subscription.table.value'),
			cell: ValueCell
		},
		{
			accessorKey: 'status',
			header: t('billing.subscription.table.status'),
			cell: ({ row }) => StatusCellComponent(row)
		},
		{
			id: 'data',
			accessorKey: 'data',
			aggregationFn: 'unique',
			header: '',
			cell: ({ row }) => MobileCellComponent(row)
		},
		{
			accessorKey: 'buttons',
			header: '',
			cell: ({ row }) => ButtonsCellComponent(row)
		}
	];

	useLayoutEffect(() => {
		if (mdDown) {
			setGrouping([
				'date',
				'reference',
				'billingRange',
				'amount',
				'status',
				'buttons'
			]);
		} else {
			setGrouping(['data']);
		}
	}, [mdDown]);

	return (
		<DataTable
			className="mb-20"
			columns={columns}
			data={history}
			manualGrouping
			ready={ready}
			state={{
				grouping
			}}
			onGroupingChange={setGrouping}
		/>
	);
}

export default Table;
